import React, { useEffect, useState, useMemo } from "react";
import { fetchSignals, fetchAdminUsers } from "../api/client";
import { format } from "date-fns";
import { Filter, RefreshCcw, RotateCcw, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import LockedFeatureGate from "../components/LockedFeatureGate";

const getTodayString = () => {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const INITIAL_FILTERS = {
  status: "",
  action: "",
  symbol: "",
  date_from: getTodayString(),
  date_to: getTodayString(),
  user_name: "",
  license_id: "",
  magic_number: "",
};

const EMPTY_FILTERS = {
  status: "",
  action: "",
  symbol: "",
  date_from: "",
  date_to: "",
  user_name: "",
  license_id: "",
  magic_number: "",
};

export default function Signals() {
  const { user: currentUser } = useAuth();
  
  const [data, setData] = useState({ signals: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  
  // Admin-specific state
  const [users, setUsers] = useState([]);
  const [selectedUserObj, setSelectedUserObj] = useState(null);

  // Load all users for Admin dropdown on mount
  useEffect(() => {
    if (currentUser?.role === "admin") {
      fetchAdminUsers(0, 200)
        .then((res) => {
          setUsers(res.users || []);
        })
        .catch((err) => {
          console.error("Failed to load admin users for signals dropdown:", err);
        });
    }
  }, [currentUser]);

  const load = (nextPage = page, nextFilters = filters, nextPageSize = pageSize) => {
    setLoading(true);
    fetchSignals(nextPage * nextPageSize, nextPageSize, nextFilters)
      .then((res) => {
        setData(res);
        setPage(nextPage);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(0, INITIAL_FILTERS);
  }, []);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  // Dynamic License IDs options based on user selection
  const licenseOptions = useMemo(() => {
    if (currentUser?.role === "admin") {
      if (selectedUserObj) {
        return selectedUserObj.license_ids || [];
      } else {
        return Array.from(new Set(users.flatMap((u) => u.license_ids || [])));
      }
    } else {
      return currentUser?.license_ids || [];
    }
  }, [currentUser, users, selectedUserObj]);

  // Enforce cascading changes in Admin mode
  const handleUserChange = (userName) => {
    const matchedUser = users.find((u) => u.name === userName);
    if (matchedUser) {
      setSelectedUserObj(matchedUser);
      setFilters((prev) => ({
        ...prev,
        user_name: userName,
        license_id: matchedUser.license_ids?.includes(prev.license_id) ? prev.license_id : "",
      }));
    } else {
      setSelectedUserObj(null);
      setFilters((prev) => ({
        ...prev,
        user_name: "",
      }));
    }
  };

  const handleLicenseChange = (licenseId) => {
    if (currentUser?.role === "admin" && licenseId) {
      const matchedUser = users.find((u) => u.license_ids?.includes(licenseId));
      if (matchedUser) {
        setSelectedUserObj(matchedUser);
        setFilters((prev) => ({
          ...prev,
          license_id: licenseId,
          user_name: matchedUser.name,
        }));
        return;
      }
    }
    updateFilter("license_id", licenseId);
  };

  const applyFilters = (event) => {
    event.preventDefault();
    load(0, filters);
  };

  const resetFilters = () => {
    setSelectedUserObj(null);
    setFilters(EMPTY_FILTERS);
    load(0, EMPTY_FILTERS);
  };

  const getBadge = (action) => {
    if (!action) return "badge-pending";
    const a = action.toUpperCase();
    if (a === "BUY") return "badge-buy";
    if (a === "SELL") return "badge-sell";
    return "badge-pending";
  };

  // Check if cascading other filters should be disabled in Admin view
  const isAdminAndNoUser = currentUser?.role === "admin" && !selectedUserObj;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Signals</div>
          <div className="page-sub">{data.total} total signals received from TradingView</div>
        </div>
        <button className="btn btn-ghost" onClick={() => load()} disabled={loading}>
          <RefreshCcw size={15} />
          Refresh
        </button>
      </div>

      <div className="card filter-card" style={{ marginBottom: "16px" }}>
        <div 
          className="card-header" 
          onClick={() => setShowFilters(!showFilters)} 
          style={{ cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 20px" }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Filter size={14} style={{ color: "var(--accent)" }} />
            <span style={{ fontSize: "14px", fontWeight: "700" }}>Filters & Search</span>
          </div>
          <button 
            type="button" 
            className="btn btn-ghost" 
            style={{ padding: "4px 10px", fontSize: "11px", minHeight: "auto", borderRadius: "8px", border: "1px solid var(--border)" }}
          >
            {showFilters ? "Collapse" : "Expand Filters"}
          </button>
        </div>

        {showFilters && (
          <form onSubmit={applyFilters} style={{ padding: "14px" }}>
            <div className="filter-grid" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "12px", padding: 0 }}>
              
              {/* User Filter Dropdown (Admin-only) */}
              {currentUser?.role === "admin" ? (
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: "11px" }}>User</label>
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.user_name}
                    onChange={(e) => handleUserChange(e.target.value)}
                  >
                    <option value="">Select User</option>
                    {users.map((u) => (
                      <option key={u.id} value={u.name}>
                        {u.name}
                      </option>
                    ))}
                  </select>
                </div>
              ) : null}

              {/* Cascading Dependent License ID Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>License ID</label>
                {currentUser?.role === "admin" ? (
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.license_id}
                    onChange={(e) => handleLicenseChange(e.target.value)}
                  >
                    <option value="">All Licenses</option>
                    {licenseOptions.map((lic) => (
                      <option key={lic} value={lic}>
                        {lic}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    className="form-input"
                    style={{ padding: "8px 12px", fontSize: "13px" }}
                    value={filters.license_id}
                    onChange={(e) => updateFilter("license_id", e.target.value)}
                  >
                    <option value="">All Licenses</option>
                    {(currentUser?.license_ids || []).map((lic) => (
                      <option key={lic} value={lic}>
                        {lic}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Magic Number Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Magic No</label>
                <input
                  className="form-input"
                  style={{ padding: "8px 12px", fontSize: "13px" }}
                  type="number"
                  value={filters.magic_number || ""}
                  onChange={(e) => updateFilter("magic_number", e.target.value)}
                  placeholder="123456"
                />
              </div>

              {/* Symbol Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Symbol</label>
                <div className="input-with-icon">
                  <Search size={12} style={{ left: "12px" }} />
                  <input
                    className="form-input"
                    style={{ padding: "8px 12px 8px 34px", fontSize: "13px" }}
                    value={filters.symbol}
                    onChange={(e) => updateFilter("symbol", e.target.value)}
                    placeholder="XAUUSD"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <div className="form-group" style={{ marginBottom: 0 }}>
                <label className="form-label" style={{ fontSize: "11px" }}>Status</label>
                <select 
                  className="form-input" 
                  style={{ padding: "8px 12px", fontSize: "13px" }}
                  value={filters.status} 
                  onChange={(e) => updateFilter("status", e.target.value)}
                >
                  <option value="">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="forwarded">Forwarded</option>
                  <option value="acknowledged">Acknowledged</option>
                  <option value="blocked_expired">Blocked Expired</option>
                  <option value="error">Error</option>
                </select>
              </div>

              {/* Date range row + Action Filter + filter action buttons in a single full-width line */}
              <div style={{ 
                gridColumn: "1 / -1", 
                display: "flex", 
                flexWrap: "wrap", 
                gap: "12px", 
                alignItems: "flex-end", 
                justifyContent: "space-between", 
                background: "rgba(255, 255, 255, 0.01)", 
                padding: "12px 0 0 0", 
                borderTop: "1px solid rgba(255, 255, 255, 0.04)",
                marginTop: "4px"
              }}>
                <div style={{ display: "flex", gap: "12px", flex: "1", minWidth: "360px", flexWrap: "wrap" }}>
                  {/* Action Filter */}
                  <div className="form-group" style={{ marginBottom: 0, flex: "1", minWidth: "100px" }}>
                    <label className="form-label" style={{ fontSize: "11px" }}>Action</label>
                    <select 
                      className="form-input" 
                      style={{ padding: "8px 12px", fontSize: "13px" }}
                      value={filters.action} 
                      onChange={(e) => updateFilter("action", e.target.value)}
                    >
                      <option value="">All actions</option>
                      <option value="BUY">BUY</option>
                      <option value="SELL">SELL</option>
                      <option value="CLOSE_BUY">CLOSE_BUY</option>
                      <option value="CLOSE_SELL">CLOSE_SELL</option>
                      <option value="CLOSE_ALL">CLOSE_ALL</option>
                    </select>
                  </div>

                  {/* Date From */}
                  <div className="form-group" style={{ marginBottom: 0, flex: "1", minWidth: "120px" }}>
                    <label className="form-label" style={{ fontSize: "11px" }}>Date From</label>
                    <input 
                      className="form-input" 
                      style={{ padding: "8px 12px", fontSize: "13px" }}
                      type="date" 
                      value={filters.date_from} 
                      onChange={(e) => updateFilter("date_from", e.target.value)} 
                    />
                  </div>

                  {/* Date To */}
                  <div className="form-group" style={{ marginBottom: 0, flex: "1", minWidth: "120px" }}>
                    <label className="form-label" style={{ fontSize: "11px" }}>Date To</label>
                    <input 
                      className="form-input" 
                      style={{ padding: "8px 12px", fontSize: "13px" }}
                      type="date" 
                      value={filters.date_to} 
                      onChange={(e) => updateFilter("date_to", e.target.value)} 
                    />
                  </div>
                </div>

                <div className="filter-actions" style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                  <button className="btn btn-primary" type="submit" style={{ padding: "8px 16px", fontSize: "12px" }} disabled={loading}>
                    <Filter size={13} />
                    Apply
                  </button>
                  <button className="btn btn-ghost" type="button" style={{ padding: "8px 16px", fontSize: "12px" }} onClick={resetFilters} disabled={loading}>
                    <RotateCcw size={13} />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </form>
        )}
      </div>

      {/* Signals Listing Table */}
      <div className="card">
        {loading ? (
          <div className="loading">Loading signals…</div>
        ) : data.signals.length === 0 ? (
          <div className="empty">No signals found</div>
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr style={{ background: "rgba(0, 242, 254, 0.03)" }}>
                  <th>Symbol</th>
                  <th>Action</th>
                  <th>Price</th>
                  <th>SL</th>
                  <th>TP</th>
                  <th>Lot</th>
                  <th>Magic No</th>
                  <th>User</th>
                  <th>License ID</th>
                  <th>Status</th>
                  <th>Received At</th>
                </tr>
              </thead>
              <tbody>
                {data.signals.map((s) => (
                  <tr key={s.id}>
                    <td style={{ fontWeight: 700, color: "var(--text)" }}>{s.symbol}</td>
                    <td><span className={`badge ${getBadge(s.action)}`}>{s.action}</span></td>
                    <td style={{ fontWeight: 600 }}>{s.price ?? "—"}</td>
                    <td>{s.sl ?? "—"}</td>
                    <td>{s.tp ?? "—"}</td>
                    <td>{s.lot}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12 }}>{s.magic}</td>
                    <td style={{ fontWeight: 500 }}>{s.user_name || "—"}</td>
                    <td style={{ fontFamily: "var(--mono)", fontSize: 12, color: "var(--text2)" }}>{s.license_id || "—"}</td>
                    <td>
                      <span className={`badge ${
                        s.status === "forwarded" || s.status === "acknowledged" 
                          ? "badge-ok" 
                          : s.status === "error" 
                            ? "badge-error" 
                            : "badge-pending"
                      }`}>
                        {s.status}
                      </span>
                    </td>
                    <td style={{ color: "#8b949e", fontSize: 12 }}>
                      {format(new Date(s.received_at), "yyyy-MM-dd HH:mm:ss")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {data.total > 0 && (
          <div className="pagination-row" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ color: "var(--text2)", fontSize: "13px" }}>Rows per page:</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  const newSize = parseInt(e.target.value, 10);
                  setPageSize(newSize);
                  load(0, filters, newSize);
                }}
                className="form-input"
                style={{
                  width: "80px",
                  padding: "6px 24px 6px 12px",
                  fontSize: "13px",
                  height: "34px",
                  borderRadius: "8px",
                  minHeight: "auto",
                  border: "1px solid var(--border)",
                  background: "var(--sidebar-user-bg)"
                }}
              >
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </select>
            </div>
            
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <button className="btn btn-ghost" onClick={() => load(page - 1)} disabled={loading || page === 0} style={{ padding: "6px 12px", minHeight: "auto" }}>
                Previous
              </button>
              <span style={{ display: "inline-flex", alignItems: "center", color: "var(--text2)", fontSize: "13px", padding: "0 10px", fontWeight: "700" }}>
                Page {page + 1} of {Math.max(1, Math.ceil(data.total / pageSize))}
              </span>
              <button className="btn btn-ghost" onClick={() => load(page + 1)} disabled={loading || (page + 1) * pageSize >= data.total} style={{ padding: "6px 12px", minHeight: "auto" }}>
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
